import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    Box,
    Button,
    Typography,
    List,
    ListItem,
    ListItemText,
    IconButton,
    TextField,
    InputLabel,
    MenuItem,
    Select,
    FormControl,
    Drawer,
    useMediaQuery,
    Alert,
    Tabs,
    Tab,
    Link,
    CircularProgress,
    ListItemButton,
	Tooltip,
} from "@mui/material";

import "./Rspl.css";
import PageTitle from "../../components/PageTitle/PageTitle";
import Divider from "@mui/material/Divider";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import MenuIcon from "@mui/icons-material/Menu";
import Datagrid from "../../components/Datagrid/Datagrid";
import { useCart } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { ASSETS_BASE_URL, DEFAULT_LAYOUT, RSPL_LIST } from "../../config";
import { ShoppingBasketOutlined, ShoppingCart, ShoppingCartOutlined } from "@mui/icons-material";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import { useTranslation } from "react-i18next";
import { ALERT_MESSAGES, BUTTON, LABELS, MENU_ITEMS, PLACEHOLDER, TABLE_HEADERS, TITLES, UI_TEXT } from "../../constants";
import { getMachinesBySiteId } from "../../services/manageSiteMachinesService";
import { createRecommendation, getRecommendationsByUserAndSite } from "../../services/recommendationsService";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";
import { addProductsToRecommendationList, saveRequestListBulk } from "../../services/rsplService";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { useOptions } from "../../context/optionContext";
import {
	buildRsplCodeToLabelMap,
	getRsplLabelForCode,
	parseRsplList,
} from "../../utilities/rspl";

import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import ListPageSkeleton from "../../components/PageSkeletons/ListPageSkeleton";
import { toast } from "../../context/ToastContext";

const initialItems = [];

function Rspl() {
	const { t } = useTranslation();
	const isLayout2 = String(DEFAULT_LAYOUT || "").toLowerCase() === "layout2";
	const rsplPageTitle = isLayout2
		? t('LABELS.MY_RSPL', { defaultValue: 'My RSPL' })
		: t('TITLES.RECOMMENDED_SPARE_PARTS_LISTS');
	const [items, setItems] = useState(initialItems);
	const [selectedItem, setSelectedItem] = useState([]);
	const [listSearchTerm, setListSearchTerm] = useState("");
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [sortOption, setSortOption] = useState("");
	const [tabValue, setTabValue] = useState(t('MENU_ITEMS.ALL'));
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedProductRows, setSelectedProductRows] = useState([]);
	const isMobile = useMediaQuery("(max-width:600px)");
	const { selectedSite, siteData, selectedSiteCurrency, currencySymbol } = useSite();
	const { addMultipleToCart } = useCart();
	const { user, hasPermission } = useAuth();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [listItems, setListItems] = useState([]);
	const [selectedList, setSelectedList] = useState(null);
	const [isCreatingNewList, setIsCreatingNewList] = useState(false);
	const [newListName, setNewListName] = useState("");
	const [newListDescription, setNewListDescription] = useState("");
	const [newListError, setNewListError] = useState(""); // Added this line
	const [firstLoad, setFirstLoad] = useState(true);
	const [spareParts, setSpareParts] = useState({});
	const [isSavingList, setIsSavingList] = useState(false);
	const selectedDrawerItemRef = useRef(null);
	const selectedDesktopItemRef = useRef(null);
	const navigate = useNavigate();
	const triggerToast = (message, severity = "success") => {
		if (!message) return;
		toast.trigger({ message, severity });
	};
	const { stockImageUrl } = useBrandingAssets();
	const { optionKeys } = useOptions();
	const resolvedRsplList = useMemo(
		() => parseRsplList(optionKeys?.rsplList, RSPL_LIST),
		[optionKeys?.rsplList]
	);
	const rsplMapping = useMemo(
		() => buildRsplCodeToLabelMap(resolvedRsplList),
		[resolvedRsplList]
	);
	useEffect(() => {
		if (!selectedItem?.machine_id) return;
		if (firstLoad) return;

		const getScrollParent = (el) => {
			let node = el?.parentElement;
			while (node) {
				const style = window.getComputedStyle(node);
				const overflowY = style.overflowY;
				const canScroll = (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight;
				if (canScroll) return node;
				node = node.parentElement;
			}
			return null;
		};

		const centerItem = (itemEl) => {
			if (!itemEl) return;
			const container = getScrollParent(itemEl);
			if (!container) return;
			const containerRect = container.getBoundingClientRect();
			const itemRect = itemEl.getBoundingClientRect();
			const itemTopWithinContainer = itemRect.top - containerRect.top;
			const targetScrollTop =
				container.scrollTop +
				itemTopWithinContainer -
				(container.clientHeight / 2 - itemRect.height / 2);

			container.scrollTo({
				top: Math.max(0, targetScrollTop),
				behavior: "smooth",
			});
		};

		let raf1 = 0;
		let raf2 = 0;
		raf1 = window.requestAnimationFrame(() => {
			raf2 = window.requestAnimationFrame(() => {
				centerItem(selectedDrawerItemRef.current);
				centerItem(selectedDesktopItemRef.current);
			});
		});
		return () => {
			window.cancelAnimationFrame(raf1);
			window.cancelAnimationFrame(raf2);
		};
	}, [selectedItem?.machine_id, firstLoad, loading, items.length, listSearchTerm, drawerOpen, isMobile]);

	const canAddToList = hasPermission("recommendations_management.add_parts")
	const canCreateList = hasPermission("recommendations_management.create_list")

	const sortOptions = useMemo(
		() => ([
			t('UI_TEXT.MACHINE_NAME_ASC', { defaultValue: 'Machine Name Asc' }),
			t('UI_TEXT.MACHINE_NAME_DESC', { defaultValue: 'Machine Name Desc' }),
			t('UI_TEXT.MACHINE_NUMBER_ASC', { defaultValue: 'Machine Number Asc' }),
			t('UI_TEXT.MACHINE_NUMBER_DESC', { defaultValue: 'Machine Number Desc' }),
		]),
		[t]
	);
	const sortCollator = useMemo(
		() => new Intl.Collator(undefined, { sensitivity: "base", numeric: true, ignorePunctuation: true }),
		[]
	);
	const normalizeSortText = (value) =>
		String(value || "")
			.normalize("NFKD")
			.replace(/[\u0300-\u036f]/g, "")
			.replace(/\s+/g, " ")
			.trim();
	const sortMachinesByOption = (machines = [], option) => {
		const [nameAsc, nameDesc, numAsc, numDesc] = sortOptions;
		const rows = Array.isArray(machines) ? [...machines] : [];
		const compareByName = (a, b) => {
			const nameDiff = sortCollator.compare(
				normalizeSortText(a?.machine_name),
				normalizeSortText(b?.machine_name)
			);
			if (nameDiff !== 0) return nameDiff;
			return sortCollator.compare(
				normalizeSortText(a?.machine_number),
				normalizeSortText(b?.machine_number)
			);
		};
		const compareByNumber = (a, b) => {
			const numberDiff = sortCollator.compare(
				normalizeSortText(a?.machine_number),
				normalizeSortText(b?.machine_number)
			);
			if (numberDiff !== 0) return numberDiff;
			return sortCollator.compare(
				normalizeSortText(a?.machine_name),
				normalizeSortText(b?.machine_name)
			);
		};
		if (option === nameDesc) {
			return rows.sort((a, b) => compareByName(b, a));
		}
		if (option === numAsc) {
			return rows.sort(compareByNumber);
		}
		if (option === numDesc) {
			return rows.sort((a, b) => compareByNumber(b, a));
		}
		return rows.sort(compareByName);
	};
	const columns = [
		{
			field: "product_name",
			headerName: t('TABLE_HEADERS.PART_NAME'),
			flex: 1,
			renderCell: (params) => {
				const defaultImage = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;

				const handleError = (e) => {
					e.target.onerror = null; // Prevent infinite loop
					e.target.src = defaultImage; // Fallback to default image
				};

				const src = (() => {
					const raw = params.row?.asset_data?.images?.[0];
					if (raw) {
						if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) return raw;
						const path = raw.startsWith("/") ? raw : `/${raw}`;
						return `${ASSETS_BASE_URL}${path}`;
					}
					return `${ASSETS_BASE_URL}/ProductImages/TMB/${params.row.product_number}.JPG`;
				})();

				return (
					<div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "flex-start" }}>
						<img
							src={src}
							alt={params.row.product_name}
							onError={handleError}
							style={{
								width: "40px",
								height: "40px",
								objectFit: "cover",
								borderRadius: "4px",
							}}
						/>
						<div>
							<Link
								sx={{ cursor: "pointer" }}
								variant="body2"
								onClick={() => {
									navigate(`/machines/${params.row.machine_id}/${params.row.product_id}`);
								}}>
								{params.row.product_name}
							</Link>
						</div>
					</div>
				);
			},
		},
		{
			field: "product_number",
			headerName: t('UI_TEXT.PART'),
			width: 200,
		},
		{
			field: "product_sell_price",
			headerName: t('TABLE_HEADERS.PRICE'),
			width: 200,
			renderCell: (params) => {
				const key = `price_${selectedSiteCurrency}`;
				const currencyPrice = params.row[key];

				// If the product is purchasable, use the currency price; otherwise, set it to 0
				const finalPrice = params.row.purchasable ? currencyPrice : 0;
				if (params.row.purchasable === 1) {
					return (
						<div>
							{currencySymbol}
							{finalPrice}
						</div>
					);
				} else {
					return null;
				}
			},
		},
	];

	const currentTabProducts = (() => {
		if (tabValue === t('MENU_ITEMS.ALL')) return Object.values(spareParts || {}).flat();
		return Array.isArray(spareParts?.[tabValue]) ? spareParts[tabValue] : [];
	})();
	const hasAnyProducts = Object.values(spareParts || {}).some((arr) => Array.isArray(arr) && arr.length > 0);
	const showNoProductsAlert = selectedItem && (!hasAnyProducts || currentTabProducts.length === 0);

    useEffect(() => {
        const fetchMachines = async () => {
            try {
                const reqBody = {
                    site_number: selectedSite,
                };

				const response = await getMachinesBySiteId(selectedSite);
				console.log("Inside rspl getMachineById response", response);
				if (response?.status) {
					const uniqueMachines = response.data.filter(
						(machine, index, self) =>
							index === self.findIndex((m) => m.machine_name === machine.machine_name)
					);
					const defaultSortOption = sortOption || sortOptions[0];
					const sortedMachines = sortMachinesByOption(uniqueMachines, defaultSortOption);
					setItems(sortedMachines);
					setSortOption(defaultSortOption);
					// setSelectedItem(uniqueMachines[0] || null);


					setSelectedItem((prev) => {
						if (prev) return prev;
						return sortedMachines[0] || null;
					});
					// Trigger RSPL fetch for first machine on site change
					setFirstLoad(true);
				} else {
					const errorMessage = response?.message || "Failed to fetch machines: No success flag or unknown error.";
					setError(errorMessage);
				}
			} catch (err) {
				setError("Failed to fetch machines: " + (err.message || "Unknown network error. Is the backend running?"));
			} finally {
				setLoading(false);
			}
		};
		const fetchRecommendationsLists = async () => {
			try {
				const user_id = user.user_id;
				const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;

				if (!site_id) {
					console.error("Site ID not found");
					return;
				}

				// Fetch recommendations
				const recommendationsResponse = await getRecommendationsByUserAndSite(user_id,site_id);
				console.log("Inside rspl getRecomendations by user and site response", recommendationsResponse);
				if (recommendationsResponse.status) {
					let recommendations = [];

					recommendations = recommendationsResponse.data.filter((item) => item.user_id === user.user_id);


					setListItems(recommendations);

					if (recommendations.length > 0) {
						const firstRecommendation = recommendations[0];
						setSelectedList(firstRecommendation);
					}
				} else {
					const errorMessage = recommendationsResponse?.message || "Failed to fetch recommendations: No success flag or unknown error.";
					console.error("Failed to fetch recommendations:", errorMessage);
					setError(errorMessage); // Set error state to display to user
				}
			} catch (error) {
				console.error("Error fetching recommendations or products:", error);
				setError("Error fetching recommendations: " + (error.message || "Unknown network error. Is the backend running?"));
			}
		};

        // Reset state immediately on site change to avoid stale selection/content
        setLoading(true);
        setItems([]);
        setSelectedItem(null);
        setSpareParts({});
        setTabValue(t('MENU_ITEMS.ALL'));
        setFirstLoad(true);

        if (selectedSite !== "") {
            fetchMachines();
            fetchRecommendationsLists();
        } else {
            setLoading(false);
        }
    }, [selectedSite, siteData]);

	const handleSortChange = (value) => {
		setSortOption(value);
		setItems((prevProducts) => sortMachinesByOption(prevProducts, value));
	};

	const getProductNumbers = (products, prods) => {
		return products
			.map((product) => {
				const prod = prods.find((p) => p.product_id === product.product_id);
				return prod ? prod.product_number : null;
			})
			.filter((productNumber) => productNumber !== null) // Remove null values if no match is found
			.join(",");
	};

	const handleSaveList = async () => {
		if (isSavingList) return;
		setIsSavingList(true);
		try {
			let response1 = undefined; // declared like this for new list response setSelectedList does not update in time
			if (isCreatingNewList) {
				const user_id = user.user_id;
				const site_id = siteData.filter((i) => i.customer_site_number === selectedSite)[0]?.site_id;
				if (!site_id) {
					triggerToast(t('ALERT_MESSAGES.FAILED_TO_CREATE_THE_LIST'), "error");
					setIsSavingList(false);
					return;
				}
				const newList = {
					name: newListName,
					description: newListDescription || "",
					user_id,
					site_id,
				};
				const normalizedName = newListName.trim().toLowerCase();
				const listExists = listItems.some(
					(item) =>
						item?.name?.trim().toLowerCase() === normalizedName &&
						item?.site_id === site_id && // Composite key check
						item?.user_id === user_id
				);
				if (listExists) {
					// Use setNewListNameError for specific feedback
					setNewListError(t('ALERT_MESSAGES.LIST_NAME_ALREADY_EXISTS'));
					setIsSavingList(false);
					return;
				}
				try {
					response1 = await createRecommendation(newList);
					console.log("Inside rspl create recomendation response", response1);
					if (response1.status) {
						setListItems((prev) => [...prev, response1.data]);
						setSelectedList(response1.data);
					} else {
						// Handle backend error from createRecommendation
						triggerToast(t('ALERT_MESSAGES.ERROR_CREATING_LIST'), "error");
						setDialogOpen(false);
						setIsSavingList(false);
						return;
					}
				} catch (error) {
					// Handle network errors or other unexpected errors during createRecommendation
					if (error.response?.status === 409) {
						setNewListError(t('ALERT_MESSAGES.LIST_NAME_ALREADY_EXISTS'));
					} else {
						triggerToast(t('ALERT_MESSAGES.ERROR_CREATING_LIST'), "error");
					}
					setDialogOpen(false);
					setIsSavingList(false);
					return;
				}
			}

			let prodsSource = [];
			if (tabValue === t('MENU_ITEMS.ALL')) {
				prodsSource = (Object.values(spareParts || {})).flat();
			} else {
				prodsSource = spareParts[tabValue];
			}
			// Ensure prods is an array and filter out any null/undefined entries
			const prods = (Array.isArray(prodsSource) ? prodsSource : []).filter(p => p !== null && p !== undefined); const filteredProducts = prods
				.filter((product) => product && selectedProductRows.includes(product.product_id))
				.map((product) => ({
					product_id: product.product_id,
					machine_id: product.machine_id,
				}));

			if (filteredProducts.length === 0) {
				triggerToast(t('ALERT_MESSAGES.NO_PARTS_SELECTED'), "warning");
				setIsSavingList(false);
				return;
			}

			const targetListId = response1 ? response1.data.id : selectedList?.id;
			if (!targetListId) {
				triggerToast(t('ALERT_MESSAGES.ERROR_CREATING_LIST'), "error");
				setIsSavingList(false);
				return;
			}
			const response = await addProductsToRecommendationList(
				targetListId,
				filteredProducts
			);
			console.log("Inside rspl ad prod to reco list response", response);
			if (response.status) {
				setDialogOpen(false);
				setIsCreatingNewList(false);
				setNewListName("");
				setNewListDescription("");

				const alreadyExisting = response.data.filter((res) => res.status === "exists");
				const newProductsAdded = response.data.filter((res) => res.status !== "exists");

				if (newProductsAdded.length > 0) {
					Swal.fire({
						icon: "success",
						title: t('ALERT_MESSAGES.SUCCESS'),
						text: t('ALERT_MESSAGES.PARTS_ADDED_TO_LIST', { defaultValue: "Part(s) added to the recommendation list." }),
					});
				}

				if (alreadyExisting.length > 0) {
					const alreadyExistingNumbers = getProductNumbers(alreadyExisting, prods);
					Swal.fire({
						icon: "info",
						title: t('ALERT_MESSAGES.INFO'),
						html: `${t('ALERT_MESSAGES.SOME_PARTS_ALREADY_EXIST')} <b>${alreadyExistingNumbers}</b>`,
					});
				}

				setSelectedProductRows([]); // Clear selection after successful save
			} else {
				// Handle error from addProductsToRecommendationList
				const errorMessage = response?.message || t('ALERT_MESSAGES.ERROR_ADDING_TO_LIST');
				Swal.fire({
					icon: "error",
					title: t('ALERT_MESSAGES.ERROR'),
					text: errorMessage,
				});
			}
		} catch (error) {
			console.error("Error in handleSaveList:", error);
			const errorMessage = error.response?.data?.message || error.message || t('ALERT_MESSAGES.ERROR_ADDING_TO_LIST');
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: errorMessage,
			});
		} finally {
			setIsSavingList(false);
		}
	};

const handleItemClick = async (item) => {
    // Clear previous selection and data to force Datagrid refresh
    setSelectedProductRows([]);
    setSpareParts({});
    setTabValue(t('MENU_ITEMS.ALL'));
    setFirstLoad(true);
    setSelectedItem(item);
};

	const normalizedListSearch = listSearchTerm.trim().toLowerCase();
	const filteredItems = useMemo(() => {
		const filtered = items.filter((item) => {
			if (!normalizedListSearch) return true;
			const name = (item?.machine_name || "").toLowerCase();
			const number = (item?.machine_number || "").toLowerCase();
			return name.includes(normalizedListSearch) || number.includes(normalizedListSearch);
		});
		return sortMachinesByOption(filtered, sortOption || sortOptions[0]);
	}, [items, normalizedListSearch, sortOption, sortOptions]);

	useEffect(() => {
		const fetchRSPLParts = async (item) => {
			try {
				const reqBody = {
					machine_id: item.machine_id,
					site_number: selectedSite
				};
				const sparePartsDict = {};
				const response = await getProductsByMachineId(reqBody);
				console.log("Inside rspl get prod by machine id response", response);
				if (response.status) {
					const { products: machineProducts } = normalizeMachineProductsResponse(response);
					(resolvedRsplList || []).forEach((type) => {
						sparePartsDict[type.value] = [];
					});
					machineProducts.forEach((product) => {
						const typeLabel = getRsplLabelForCode(
							product?.product_rspl_code,
							rsplMapping,
							""
						);
						if (!typeLabel || !sparePartsDict[typeLabel]) return;
						sparePartsDict[typeLabel].push(product);
					});
					setSpareParts(sparePartsDict);
					setFirstLoad(false);
					const firstRsplWithParts = (resolvedRsplList || []).find(
						(type) => Array.isArray(sparePartsDict?.[type.value]) && sparePartsDict[type.value].length > 0
					);
					setTabValue(firstRsplWithParts?.value || t('MENU_ITEMS.ALL'));
				}
			} catch (error) {
				console.error("Error fetching products:", error);
			}
		};
		if (selectedItem && firstLoad) {
			fetchRSPLParts(selectedItem);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedItem, firstLoad, resolvedRsplList, rsplMapping, selectedSite, t]);

	useEffect(() => {
		if (items.length > 0 && !selectedItem) {
			setSelectedItem(items[0]);
		}
	}, [items]);

	const handleListSearchChange = (value) => {
		setListSearchTerm(value);
	};

	const handleAddToCart = async (id) => {
		const purchasableProducts = [];
		const nonPurchasableProducts = [];
		var message = "Products added to";
		selectedProductRows.forEach((productId) => {
			const prods = tabValue === t('MENU_ITEMS.ALL') ? Object.values(spareParts).flat() : spareParts[tabValue];
			const product = prods.find((product) => product.product_id === productId);
			if (product) {
				if (product.purchasable === 1) {
					const productEntry = {
						ProductID: product.product_id,
						Quantity: 1,
						Site: selectedSite,
					};
					purchasableProducts.push(productEntry);
				} else {
					const productEntry = {
						machine_id: product.machine_id,
						product_id: product.product_id,
						site_number: selectedSite,
						user_id: user.user_id,
					};
					nonPurchasableProducts.push(productEntry);
				}
			}
		});

		if (purchasableProducts.length > 0) {
			await addMultipleToCart(purchasableProducts);
			message += " Cart successfully!";
			Swal.fire({
				icon: "success",
				title: t('ALERT_MESSAGES.SUCCESS'),
				text: message,
			});
			setSelectedProductRows([]); 
		}
		if (nonPurchasableProducts.length > 0) {
			Swal.fire({
				icon: "info",
				text: t('ALERT_MESSAGES.SOME_PART_CAN_NOT_BE_PURCHASED'),
				confirmButtonText: t('MENU_ITEMS.YES'),
				showDenyButton: true,
				denyButtonText:t('MENU_ITEMS.NO'),
			}).then(async (results) => {
				if (results.isConfirmed) {
					await handleAddToRequest(nonPurchasableProducts);
				}
			});
		}
	};

	const handleRowSelect = (rows) => {
		setSelectedProductRows(rows);
	};

	const handleAddToRequest = async (arg) => {
		try {
			const reqBody = {
				products: arg,
			};

			const response = await saveRequestListBulk(arg);
			console.log("Inside rspl save request list bulk response", response);
			if (response.status) {
				Swal.fire({
					icon: "success",
					title: t('ALERT_MESSAGES.SUCCESS'),
					text:  t('GENERAL.PARTS_ADDED_TO_REQUEST_LIST'),
				});
				setSelectedProductRows([]);
			}
		} catch (error) {
			console.error("Error Adding products to Request list:", error);
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: t('ALERT_MESSAGES.ERROR_ADDING_TO_LIST'),
			});
		}
	};

	const toggleDrawer = () => {
		setDrawerOpen(!drawerOpen);
	}; 

	const handleTabChange = (event, newValue) => {
		setTabValue(newValue);
	};

	if (loading || (selectedItem && firstLoad )) {
		return <ListPageSkeleton showAction={false} />;
	}

	if (error && !loading) {
		return (
			<Alert severity="error" className="error-alert">
				{error}
			</Alert>
		);
	}
	return (
		<>
			<Box className="recommendation-page" sx={{ padding: isMobile ? "10px" : "40px" }}>
				<PageTitle title={rsplPageTitle} />

				<Box display="flex" justifyContent={isMobile ? "space-between" : "flex-end"} mb={2}>
					{isMobile && (
						<IconButton onClick={toggleDrawer}>
							<MenuIcon />
						</IconButton>
					)}
				</Box>

				<Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer}>
					<Box width="250px" p={2}>
						<ContextFilterAndSort
							value={listSearchTerm}
							onSearchChange={handleListSearchChange}
							placeholder={t('PLACEHOLDER.SEARCH_LIST')}
							options={sortOptions}
							selectedOption={sortOption}
							onSortChange={handleSortChange}
							showSort
							showSearch
						/>
						<List
							style={{
								overflowY: "auto",
								height: "calc(100% - 48px)",
								maxHeight: "500px",
							}}>
							{filteredItems.length > 0 ? (
								filteredItems.map((item) => (
									<ListItem
										key={item.machine_id}
										ref={selectedItem?.machine_id === item.machine_id ? selectedDrawerItemRef : null}
										button
										onClick={() => handleItemClick(item)}
										style={{
											cursor: "pointer",
											backgroundColor: selectedItem?.machine_id === item.machine_id ? "#e8e8e8" : "transparent",
											height: "45px",
										}}
										sx={{
											"&:hover": {
												color: "var(--primary-main)",
												transition: "0.2s",
											},
										}}>
										<ListItemText
											primary={item.machine_name}
											primaryTypographyProps={{
												style: {
													color:
														selectedItem?.machine_id === item.machine_id
															? "var(--primary-main)"
															: "inherit",
													fontWeight: "bold",
													overflow: "hidden",
													whiteSpace: "nowrap",
													textOverflow: "ellipsis",
												},
											}}
											title={item.machine_name}
										/>
									</ListItem>
								))
							) : (
								<div
									style={{
										textAlign: "center",
										padding: "20px",
										color: "gray",
										fontStyle: "italic",
											fontSize: "12px",
										height: "100%",
									}}>
									{t('UI_TEXT.NO_MACHINE_FOUND')}
								</div>
							)}
						</List>
					</Box>
				</Drawer>

				<Box display="flex" height="inherit">
					{!isMobile && (
						<Box
							width={isMobile ? "100%" : "20%"}
							pr={2}
							style={{
								padding: "8px",
								height: "inherit",
								overflowY: "auto",
								maxHeight: "inherit",
							}}
							data-tour="product-machines">
							<Box display="flex" justifyContent="space-between" alignItems="center">
								<ContextFilterAndSort
									value={listSearchTerm}
									onSearchChange={handleListSearchChange}
									placeholder={t('PLACEHOLDER.SEARCH_LIST')}
									options={sortOptions}
									selectedOption={sortOption}
									onSortChange={handleSortChange}
									showSort
									showSearch
								/>
							</Box>
							<List
								style={{
									overflowY: "auto",
									height: "calc(100% - 48px)",
									maxHeight: "500px",
								}}>
								{filteredItems.length > 0 ? (
									filteredItems.map((item) => (
										<ListItemButton
											key={item.machine_id}
											ref={selectedItem?.machine_id === item.machine_id ? selectedDesktopItemRef : null}
											selected={selectedItem?.machine_id === item.machine_id}
											onClick={() => handleItemClick(item)}
											sx={{
												my: 0.25,
												cursor: "pointer",
												borderRadius: "10px",
												"&:hover": {
													color: "var(--primary-main)",
													transition: "0.2s",
												},
											}}>
											<Box
												sx={{
													display: 'flex',
													justifyContent: 'flex-start',
													alignItems: 'flex-start',
													flexDirection: 'column',
													rowGap: 0,
													columnGap: 2,
													px: 1,
													py: 0.25,
													color: selectedItem?.machine_id === item.machine_id ? 'primary.main' : '#252529',
													'& svg': {
														color: selectedItem?.machine_id === item.machine_id ? 'primary.main' : '#252529',
													},
												}}
											>
												<Typography variant="body1" fontWeight={"bold"}>
													{item.machine_name}
												</Typography>
												<Typography variant="body1" color={selectedItem?.machine_id === item.machine_id ? 'primary.main' : "text.secondary"} fontSize={"11px"}>
													<b>{t('UI_TEXT.MACHINE')}:</b> {item?.machine_number || "N/A"}
												</Typography>

											</Box>

										</ListItemButton>
									))
								) : (
									<div
										style={{
											textAlign: "center",
											padding: "20px",
											color: "gray",
											fontStyle: "italic",
												fontSize: "12px",
											height: "100%",
										}}>
										{t('UI_TEXT.NO_MACHINE_FOUND')}
									</div>
								)}
							</List>
						</Box>
					)}

					{!isMobile && <Divider flexItem orientation="vertical" sx={{ ml: 3, my: 0.5 }} />}

					<Box
						width={isMobile ? "100%" : "80%"}
						height="100%"
						pl={2}
						py={2}
						borderRadius={1}
						justifyContent="flex-start"
						style={{ borderColor: "#ddd", height: "100%" }}
						display="flex"
						flexDirection="column">
						<Box
							display="flex"
							justifyContent="space-between"
							mb={2}
							alignItems={isMobile ? "flex-start" : "center"}
							flexDirection={isMobile ? "column" : "row"}
							width={isMobile ? "100%" : "auto"}>
							<Tabs value={tabValue} onChange={handleTabChange} aria-label="icon label tabs example" data-tour="product-part-filters">
								{resolvedRsplList.map((list) => {
									if (Object.keys(spareParts).length !== 0 && spareParts[list.value].length > 0) {
										return <Tab label={list.value} value={list.value} key={list.value} />;
									}
									return null;
								})}
								<Tab label={t('MENU_ITEMS.ALL')} value={t('MENU_ITEMS.ALL')} key={t('MENU_ITEMS.ALL')} />
							</Tabs>
							<Box
								display="flex"
								flexDirection="row"
								justifyContent={isMobile ? "space-between" : ""}
								gap="10px"
								width={isMobile ? "100%" : "auto"}
								sx={{ marginTop: isMobile ? 2 : 0 }}
								data-tour="rspl-action-buttons">
								{canCreateList && canAddToList && (
									<Tooltip title={t('BUTTON.ADD_TO_RECOMMENDATION')} arrow>
										<span>
											<Button
												onClick={() => setDialogOpen(true)}
												variant="contained"
												// size="medium"
												startIcon={<FormatListBulletedIcon />}
												disabled={selectedProductRows.length < 1}>
												{t('BUTTON.ADD_TO_RECOMMENDATION')}
											</Button>
										</span>
									</Tooltip>
								)}
								<Tooltip title={t('BUTTON.ADD_TO_CART')} arrow>
									<span>
										<Button
											onClick={handleAddToCart}
											variant="contained"
											// size="medium"
											startIcon={<ShoppingCart />}
											disabled={selectedProductRows.length < 1}>
											{t('BUTTON.ADD_TO_CART')}
										</Button>
									</span>
								</Tooltip>
							</Box>
						</Box>

						<ModalWrapper
							open={dialogOpen}
							setOpen={setDialogOpen}
							title={t('UI_TEXT.SELECT_CREATE_LIST')}
							content={
								<>
									{newListError && (
										<Alert severity="error" sx={{ mb: 2 }}>
											{newListError}
										</Alert>
									)}
									{!isCreatingNewList ? (
										<>
											<FormControl fullWidth margin="dense">
												<InputLabel id="select-list-label">{t('LABELS.SELECT_LIST')}</InputLabel>
												<Select
													labelId="select-list-label"
													value={selectedList?.id || ""}
													size="small"
													onChange={(e) => {
														if (e.target.value === "newList") {
															setIsCreatingNewList(true);
														} else {
															const selected = listItems.find((i) => String(i.id) === String(e.target.value));
															setSelectedList(selected || null);
														}
													}}
													label={t('LABELS.SELECT_LIST')}>
													{listItems.map((item) => (
														<MenuItem key={item.id} value={item.id}>
															{item.name}
														</MenuItem>
													))}
													{canCreateList && <MenuItem key={"newList"} value={"newList"}>
														{t('MENU_ITEMS.CREATE_NEW_LIST')}
													</MenuItem>}
												</Select>
											</FormControl>
											<Divider sx={{ margin: "10px" }}>{t('LABELS.OR')}</Divider>
											<Button onClick={() => setIsCreatingNewList(true)} variant="outlined" fullWidth>
												{t('BUTTON.CREATE_NEW_RECOMMENDATION_LISTS')}
											</Button>
										</>
									) : (
										<Box>
											<Button
												onClick={() => {
													setIsCreatingNewList(false);
													setNewListError("");
												}}
												variant="text"
												fullWidth
												sx={{ mb: 1 }}
											>
												{t('LABELS.SELECT_LIST')}
											</Button>
											<TextField
												fullWidth
												label={t('LABELS.LIST_NAME')}
												value={newListName}
												onChange={(e) => {
													setNewListName(e.target.value);
													if (newListError) setNewListError("");
												}}
												sx={{ marginBottom: 2, marginTop: 2 }}
											/>
											<TextField
												fullWidth
												label={t('LABELS.LIST_DESCRIPTION')}
												value={newListDescription}
												onChange={(e) => setNewListDescription(e.target.value)}
												sx={{ marginBottom: 2 }}
											/>
										</Box>
									)}
								</>
							}
							actions={
								<>
									<Button
										onClick={() => {
											setDialogOpen(false);
											setIsCreatingNewList(false);
											setNewListName("");
											setNewListDescription("");
											setNewListError("");
										}}
										variant="outlined"
									>
										{t('BUTTON.CANCEL')}
									</Button>
									<Button
										onClick={handleSaveList}
										variant="contained"
										disabled={
											isSavingList ||
											(isCreatingNewList ? !newListName.trim() || !newListDescription.trim() : !selectedList)
										}
									>
										{isSavingList ? (
											<>
												<CircularProgress size={16} sx={{ color: "white", mr: 1 }} /> {t('BUTTON.SAVING')}
											</>
										) : (
											t('BUTTON.SAVE')
										)}
									</Button>
								</>
							}
						/>

						{selectedItem ? (
							<Box
								display="flex"
								flexWrap="wrap"
								justifyContent={isMobile ? "center" : "flex-start"}
								paddingLeft="15px"
								gap="10px"
								sx={{ height: "100%" }}>
								{showNoProductsAlert ? (
									<Box
										display="flex"
										justifyContent="center"
										alignContent="center"
										width="100%"
										alignItems="center"
										height="400px">
										<Alert severity="info">{t('ALERT_MESSAGES.NO_PRODUCTS_FOR_SELECTED_MACHINE')}</Alert>
									</Box>
								) : (
									// <Box data-tour="rspl-part-selection" sx={{ width: '100%', height: '400px' }}>
										<Datagrid
											key="product_id"
											data={currentTabProducts}
											checkboxSelection={true}
											columns={columns}
											onSelect={handleRowSelect}
											rowSelectionModel={selectedProductRows}
											rowIdField="product_id"
											sortBy={[{ field: "ProductName", sort: "asc" }]}
											sx={{ height: "400px" }}
											hideFilterFromColumnMenu
											lockActiveColumnInManageColumns
											preventHidingLastColumn
										/>
									// </Box>
								)}
							</Box>
						) : (
							<Typography variant="body1" color="textSecondary">
								{t('UI_TEXT.SELECT_AN_ITEM_FROM_THE_LIST')}
							</Typography>
						)}
					</Box>
				</Box>
			</Box>
		</>
	);
}

export default Rspl;
